import { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useSessionStore } from '../stores/sessionStore';
import { authApi } from '../features/auth/authApi';
import { hasPlatformAccess } from '../features/auth/platformAccess';
import { tasksApi } from '../features/tasks/tasksApi';
import { navigationApi } from '../features/navigation/navigationApi';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { BrandLogo } from './BrandLogo';
import { hasModuleAccess } from '../config/moduleRegistry';
import {
  LayoutDashboard,
  MessageSquare,
  Users,
  Building2,
  Star,
  DollarSign,
  GitBranch,
  GitPullRequest,
  CheckSquare,
  Calendar,
  Tag,
  Zap,
  Megaphone,
  CreditCard,
  BarChart,
  Settings2,
  Brain,
  MessageCircle,
  Bot,
  Link,
  Layout,
  Shield,
  Webhook,
  Box,
  Repeat,
  PanelLeftClose,
  PanelLeftOpen,
  Search,
  Radar,
  PanelsTopLeft,
  BadgeDollarSign,
  HeartPulse,
  Siren,
  TrendingUp,
} from 'lucide-react';

const SIDEBAR_KEY = 'crm-sidebar-collapsed';

export function Sidebar({ collapsed = false }: { collapsed?: boolean }) {
  const { t } = useTranslation();
  const user = useSessionStore((state) => state.user);
  const session = useSessionStore((state) => state.session);
  const token = session?.access_token;
  const [searchTerm, setSearchTerm] = useState('');

  // Status centralizado de pendências (manual vs automação) para todos os módulos
  const navStatusQuery = useQuery({
    queryKey: ['navigation-status', token],
    enabled: Boolean(token),
    queryFn: async () => await navigationApi.getStatus(token!),
    refetchInterval: 30000,
    staleTime: 10000,
  });

  const tasksQuery = useQuery({
    queryKey: ['tasks', token],
    enabled: Boolean(token),
    queryFn: async () => await tasksApi.list(token!),
    staleTime: 30000,
  });

  const modulesStatus = navStatusQuery.data?.modules;
  const pendingTasksCount = tasksQuery.data?.summary?.pending ?? 0;

  // Determinação de status para tarefas:
  // Vermelho: se houver pendência manual da equipe (tasksManual > 0 ou pendingTasksCount > 0)
  // Amarelo: se estiver em fila aguardando ser resolvida por automação/IA
  const tasksBadgeStatus = (pendingTasksCount > 0 || modulesStatus?.tasks?.status === 'manual')
    ? 'manual'
    : (modulesStatus?.tasks?.status === 'automated' ? 'automated' : 'none');

  const isTablet = useMediaQuery('(max-width: 1280px) and (min-width: 769px)');
  const isEffectivelyCollapsed = collapsed || isTablet;

  const navigationSections = [
    {
      title: t('sections.workspace', 'Workspace'),
      items: [
        { to: '/', label: t('sidebar.dashboard'), icon: LayoutDashboard },
        { 
          to: '/conversations', 
          label: t('sidebar.conversations'), 
          icon: MessageSquare, 
          badgeStatus: modulesStatus?.conversations?.status || 'none',
          badgeDetail: modulesStatus?.conversations?.detail,
          moduleId: 'inbox_conversations' 
        },
        { to: '/channels', label: t('sidebar.channels'), icon: MessageCircle, moduleId: 'inbox_channels' },
        { to: '/social-opportunities', label: 'Oportunidades sociais', icon: Radar, moduleId: 'inbox_social_ops' },
        { to: '/social-health', label: 'Saúde social', icon: HeartPulse, moduleId: 'inbox_social_health' },
        { 
          to: '/incidents', 
          label: 'Incidentes', 
          icon: Siren, 
          badgeStatus: modulesStatus?.incidents?.status || 'none',
          badgeDetail: modulesStatus?.incidents?.detail,
          moduleId: 'ops_incidents' 
        },
        { 
          to: '/deploy-requests', 
          label: 'Deploy & Repos', 
          icon: GitPullRequest, 
          badgeStatus: modulesStatus?.deploy_requests?.status || 'none',
          badgeDetail: modulesStatus?.deploy_requests?.detail,
          moduleId: 'ops_tasks' 
        },
        { 
          to: '/tasks', 
          label: t('sidebar.tasks'), 
          icon: CheckSquare, 
          badgeStatus: tasksBadgeStatus,
          badgeDetail: modulesStatus?.tasks?.detail || (pendingTasksCount > 0 ? `${pendingTasksCount} tarefa(s) pendente(s) manual` : undefined),
          moduleId: 'ops_tasks' 
        },
        { 
          to: '/schedules', 
          label: t('sidebar.schedules'), 
          icon: Calendar, 
          badgeStatus: modulesStatus?.schedules?.status || 'none',
          badgeDetail: modulesStatus?.schedules?.detail,
          moduleId: 'ops_schedules' 
        },
      ],
    },
    {
      title: t('sections.crm', 'CRM'),
      items: [
        { to: '/lead-sources', label: 'Fontes', icon: Webhook, moduleId: 'crm_lead_sources' },
        { to: '/leads', label: t('sidebar.leads'), icon: Star, moduleId: 'crm_leads' },
        { to: '/deals', label: t('sidebar.deals'), icon: DollarSign, moduleId: 'crm_deals' },
        { to: '/pipelines', label: t('sidebar.pipelines'), icon: GitBranch, moduleId: 'crm_pipelines' },
        { to: '/contacts', label: t('sidebar.contacts'), icon: Users, moduleId: 'crm_contacts' },
        { to: '/companies', label: t('sidebar.companies'), icon: Building2, moduleId: 'crm_companies' },
        { to: '/tags', label: t('sidebar.tags'), icon: Tag, moduleId: 'crm_tags' },
      ],
    },
    {
      title: t('sections.intelligence', 'Inteligência'),
      items: [
        { 
          to: '/agents', 
          label: t('sidebar.agents'), 
          icon: Bot, 
          badgeStatus: modulesStatus?.agents?.status || 'none',
          badgeDetail: modulesStatus?.agents?.detail,
          moduleId: 'ai_agents' 
        },
        { to: '/kb', label: t('sidebar.kb', 'Base de Conhecimento'), icon: Brain, moduleId: 'ai_knowledge_base' },
        { to: '/automations', label: t('sidebar.automations'), icon: Settings2, moduleId: 'ai_automations' },
        { to: '/flow-builder', label: t('sidebar.flow_builder'), icon: Zap, moduleId: 'ai_flow_builder' },
        { to: '/campaigns', label: t('sidebar.campaigns'), icon: Megaphone, moduleId: 'ai_campaigns' },
        { to: '/social-content', label: 'Conteúdo social', icon: PanelsTopLeft, moduleId: 'ai_social_content' },
        { to: '/lead-discovery', label: 'Prospecção B2B', icon: Radar, moduleId: 'ai_lead_discovery' },
        { to: '/growth-dashboard', label: 'Growth Autônomo', icon: TrendingUp, moduleId: 'ai_growth_engine' },
        { to: '/ai-packages', label: 'Pacotes & Franquia IA', icon: Zap },
      ],
    },
    {
      title: t('sections.management', 'Gestão & Admin'),
      items: [
        { to: '/finance', label: t('sidebar.finance'), icon: CreditCard, moduleId: 'fin_invoicing' },
        { to: '/subscriptions', label: 'Assinaturas', icon: Repeat, moduleId: 'fin_subscriptions' },
        { 
          to: '/expense-approvals', 
          label: 'Aprovar despesas', 
          icon: BadgeDollarSign, 
          badgeStatus: modulesStatus?.expense_approvals?.status || 'none',
          badgeDetail: modulesStatus?.expense_approvals?.detail,
          moduleId: 'fin_expense_approvals' 
        },
        { to: '/reports', label: t('sidebar.reports'), icon: BarChart, moduleId: 'analytics_reports' },
        { to: '/integrations', label: t('sidebar.external_integrations'), icon: Link, moduleId: 'ext_integrations' },
        { to: '/widgets', label: t('sidebar.widgets'), icon: Layout, moduleId: 'ext_widgets' },
        { to: '/admin', label: t('sidebar.admin'), icon: Shield, adminOnly: true },
        { to: '/webhooks', label: t('sidebar.webhooks'), icon: Webhook, moduleId: 'ext_integrations' },
        { to: '/custom-fields', label: t('sidebar.custom_fields'), icon: Box, moduleId: 'crm_custom_fields' },
      ],
    },
  ];

  const filteredSections = navigationSections
    .map((section) => ({
      ...section,
      items: section.items
        .filter((item: any) => {
          // Filtro exclusivo de admin
          if (item.adminOnly) {
            const isAdmin = hasPlatformAccess(user);
            if (!isAdmin) return false;
          }
          // Filtro por módulo contratado
          if (item.moduleId && !hasModuleAccess(user, item.moduleId)) {
            return false;
          }
          // Filtro de busca de texto
          return item.label.toLowerCase().includes(searchTerm.toLowerCase());
        }),
    }))
    .filter((section) => section.items.length > 0);

  const appVersion = import.meta.env.APP_VERSION || import.meta.env.VITE_APP_VERSION || '0.5.0';

  const activeTenantLogo = useSessionStore((state) => state.activeTenantLogo);
  const activeTenantName = useSessionStore((state) => state.activeTenantName) || user?.tenant_name || user?.tenant_id;

  return (
    <aside className={`sidebar ${isEffectivelyCollapsed ? 'sidebar-collapsed' : ''}`}>

      {/* Search */}
      {!isEffectivelyCollapsed && (
        <div className="sidebar-search">
          <Search size={14} className="sidebar-search-icon" />
          <input
            type="text"
            placeholder={t('sidebar.search_placeholder', 'Buscar módulo...')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="sidebar-search-input"
          />
        </div>
      )}

      {/* Navigation Sections */}
      <nav className="sidebar-nav">
        {filteredSections.map((section) => (
          <div key={section.title} className="sidebar-section">
            {!isEffectivelyCollapsed && (
              <span className="sidebar-section-title">{section.title}</span>
            )}
            {section.items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) =>
                  `sidebar-link ${isActive ? 'active' : ''}`
                }
                title={isEffectivelyCollapsed ? item.label : undefined}
              >
                <item.icon size={18} className="sidebar-icon" />
                {!isEffectivelyCollapsed && (
                  <span className="sidebar-label">{item.label}</span>
                )}
                {!isEffectivelyCollapsed && (
                  (item as any).badgeStatus && (item as any).badgeStatus !== 'none' ? (
                    <span 
                      className={`sidebar-badge badge-${(item as any).badgeStatus}`} 
                      title={(item as any).badgeDetail || ((item as any).badgeStatus === 'manual' ? 'Pendência manual da nossa parte' : 'Em fila aguardando automação')}
                    />
                  ) : (item as any).badge ? (
                    <span className="sidebar-badge badge-manual" title="Pendência manual da nossa parte" />
                  ) : null
                )}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>

      {/* Footer */}
      <div className="sidebar-footer" style={{ padding: '0.45rem 0.5rem', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px' }}>
        <BrandLogo 
          collapsed={isEffectivelyCollapsed} 
          height={22} 
          logoUrl={null} 
          brandName="CRM Platform Suite"
        />
        <div className="prd-hub" style={{ textAlign: 'center' }}>
          <span className="sidebar-footer-version" style={{ fontSize: '9px', fontWeight: 600 }}>v{appVersion}</span>
        </div>
      </div>
    </aside>
  );
}
